import { resolveDealExportColumns } from '@openx/components-deal/lib/DealsList/dealExportColumns';
import { useDealContext } from '@openx/components-deal/lib/utils/context/DealContext';
import type { OAListDeal } from '@openx/types/deal';
import { getExportTruncationWarning, readExportTruncation } from '@openx/utils/hooks/useExport/exportTruncation';
import { downloadBlobParts } from '@openx/utils/lib/downloadBlobParts';
import { parseMultiValueFilter } from '@openx/utils/lib/multiValueFilter';
import { OPEN_AUDIENCE_ORG_ID } from '@openx/utils/services/config';
import { useSnackbar } from 'notistack';
import { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../../components/AuthContext/context';
import { getRollbar } from '../../../modules/analytics/rollbar';
import type { DealsListCriteria } from '../../../modules/deals/DealsList/dealsCriteria';
import { buildDealLandingQueryCriteria, resolveDealLandingAccountFilter } from './dealLandingQueryCriteria';
import { serializeDealLandingQueryString } from './serializeDealLandingQueryString';
import { useDealAccountsFetch } from './useDealAccountsFetch';

const EXPORT_PATH = '/api/download/deal/landing-with-direct/export';

const OBJECT_TYPE = 'deals';

type UseDealLandingExportResult = {
  exportDeals: () => Promise<void>;
  isExporting: boolean;
};

export const useDealLandingExport = (criteria: DealsListCriteria): UseDealLandingExportResult => {
  const { t } = useTranslation();
  const { enqueueSnackbar } = useSnackbar();
  const { currentUser, session } = useAuth();
  const [isExporting, setIsExporting] = useState(false);

  const isOpenAudienceOrg = session?.exchange_session.account?.id === OPEN_AUDIENCE_ORG_ID;

  const { useDealsTableFetch } = useDealContext();
  const { columns } = useDealsTableFetch<OAListDeal, Record<string, unknown>>();

  const {
    accountUidByNetworkId,
    error: accountsError,
    ready: accountsReady,
  } = useDealAccountsFetch({ skip: !isOpenAudienceOrg });

  const hasAccountFilter = parseMultiValueFilter(criteria.filters?.account).length > 0;
  // Accounts that failed to load never turn ready, so only an in-flight request should keep the action busy.
  const waitsForAccountTranslation = hasAccountFilter && !accountsReady && !accountsError;

  const exportDeals = useCallback(async () => {
    setIsExporting(true);

    try {
      if (hasAccountFilter && !accountsReady) {
        throw new Error('Deal accounts needed to translate the Organization filter are not available');
      }

      const resolvedCriteria = resolveDealLandingAccountFilter(
        buildDealLandingQueryCriteria(criteria),
        accountUidByNetworkId
      );
      const params = new URLSearchParams(serializeDealLandingQueryString(resolvedCriteria));

      params.delete('limit');
      params.delete('offset');
      params.set('columns', resolveDealExportColumns(columns, { includeOrganization: isOpenAudienceOrg }).join(','));

      const token = await currentUser?.getIdToken();
      const response = await fetch(`${EXPORT_PATH}?${params.toString()}`, {
        headers: token ? { authorization: `Bearer ${token}` } : {},
      });

      if (!response.ok) {
        throw new Error(`Deal export request returned ${response.status}`);
      }

      downloadBlobParts(await response.blob(), OBJECT_TYPE);

      const { rowLimit, truncated } = readExportTruncation(response.headers);

      if (truncated) {
        enqueueSnackbar(getExportTruncationWarning(t, OBJECT_TYPE, rowLimit), { variant: 'warning' });

        return;
      }

      enqueueSnackbar(t('Download has completed. Please check your downloads folder.'), { variant: 'success' });
    } catch (error) {
      getRollbar()?.error('[Deals] Export failed', error);
      enqueueSnackbar(t('Export failed'), { variant: 'error' });
    } finally {
      setIsExporting(false);
    }
  }, [
    accountUidByNetworkId,
    accountsReady,
    columns,
    criteria,
    currentUser,
    enqueueSnackbar,
    hasAccountFilter,
    isOpenAudienceOrg,
    t,
  ]);

  return { exportDeals, isExporting: isExporting || waitsForAccountTranslation };
};
